import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { useMemo } from 'react'
import { ExternalCourseModelAPI } from 'models/externalCourse.model'
import Options from '../options'
import { getTranslationKey } from '../../../../../../../../../enum/courseModalities.enum'
import styles from '../index.module.scss'

interface UseColumnsProps {
  onDelete: (id: number, name: string) => void
  onDownload: (id: number) => void
}

export const CHECKBOX_COLUMN_WIDTH = 48
export const NAME_COLUMN_WIDTH = 300
export const ACTION_COLUMN_WIDTH = 45

export function useColumns({ onDelete, onDownload }: UseColumnsProps) {
  const { t } = useTranslation()
  type Column = {
    title: string
    key: keyof ExternalCourseModelAPI | 'action'
    sorteable?: boolean
    width?: number
    hideable?: boolean
    align?: 'center'
    fixed?: 'left' | 'right'
    className?: string
    render?: (...data: any) => any
  }

  const formatDate = (date?: string) => {
    if (date) {
      try {
        return format(parseISO(date), 'dd/MM/yyyy')
      } catch {
        return ''
      }
    }
    return ''
  }
  const prefix = 'administration.externalCourses.attributes'
  const prefixColumns = 'administration.externalCourses.list.table.columns'

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefix}.name`),
        key: 'name',
        sorteable: true,
        hideable: false,
        width: NAME_COLUMN_WIDTH,
        fixed: 'left',
        className: styles.nameColumn,
        render: (name, { code }) => (
          <div className={styles.name}>
            <div className={styles.nameText}>
              {name} <span className={styles.nameCode}>({code})</span>
            </div>
          </div>
        ),
      },
      {
        title: t(`${prefix}.participants`),
        key: 'participants',
        width: 170,
        sorteable: true,
        render: (participant) =>
          t(`${prefixColumns}.participants`, { count: participant }),
      },
      {
        title: t(`${prefix}.modality`),
        key: 'modality',
        width: 150,
        sorteable: true,
        render: (modality) => {
          const key = getTranslationKey(modality)
          return t(`${prefixColumns}.modalities.${key}`)
        },
      },
      {
        title: t(`${prefix}.startDate`),
        key: 'startDate',
        width: 120,
        sorteable: true,
        render: (date) => formatDate(date),
      },
      {
        title: t(`${prefix}.endDate`),
        key: 'endDate',
        width: 140,
        sorteable: true,
        render: (date) => formatDate(date),
      },
      {
        title: t(`${prefix}.institution`),
        sorteable: true,
        key: 'institution',
        width: 80,
      },
      {
        title: t(`${prefix}.additionalCode`),
        sorteable: true,
        key: 'additionalCode',
        width: 80,
      },
      {
        title: '',
        key: 'action',
        width: ACTION_COLUMN_WIDTH,
        hideable: false,
        align: 'center',
        fixed: 'right',
        className: styles.actionColumn,
        render: (
          id: ExternalCourseModelAPI['id'],
          externalCourse: ExternalCourseModelAPI
        ) => (
          <Options
            externalCourse={externalCourse}
            onDelete={() => onDelete(id, externalCourse.name)}
            onDownload={() => onDownload(id)}
          />
        ),
      },
    ],
    [t, onDelete, onDownload]
  )
  return columns
}
